import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Save, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const EMPTY = { origin: "Rhodes Airport (RHO)", destination: "", price: "", is_placeholder: false, active: true, sort: 99 };
const toPayload = (r) => ({ ...r, price: r.price === "" || r.price == null ? null : Number(r.price), sort: Number(r.sort) || 0 });

const RouteRow = ({ r, onChange, onSave, onDelete }) => (
  <tr className="border-b border-slate-100 last:border-0" data-testid={`admin-route-row-${r.id}`}>
    <td className="px-3 py-2"><input className="field-input !h-10" value={r.origin} onChange={(e) => onChange({ ...r, origin: e.target.value })} data-testid="admin-route-origin-input" /></td>
    <td className="px-3 py-2"><input className="field-input !h-10" value={r.destination} onChange={(e) => onChange({ ...r, destination: e.target.value })} data-testid="admin-route-destination-input" /></td>
    <td className="px-3 py-2"><input type="number" min={0} step="0.5" className="field-input !h-10 !w-28" value={r.price ?? ""} placeholder="Quote" onChange={(e) => onChange({ ...r, price: e.target.value, is_placeholder: false })} data-testid="admin-route-price-input" /></td>
    <td className="px-3 py-2">
      {r.is_placeholder
        ? <span className="whitespace-nowrap rounded-full bg-amber-100 px-3 py-1 text-xs text-amber-800" data-testid="admin-route-placeholder-badge">Placeholder price</span>
        : <span className="text-xs text-slate-400">Confirmed</span>}
    </td>
    <td className="px-3 py-2 text-center"><input type="checkbox" className="h-4 w-4 accent-[#C9A24B]" checked={r.active} onChange={(e) => onChange({ ...r, active: e.target.checked })} data-testid="admin-route-active-checkbox" /></td>
    <td className="px-3 py-2">
      <div className="flex gap-2">
        <button onClick={() => onSave(r)} className="flex h-10 w-10 items-center justify-center rounded-full bg-ink text-white" aria-label="Save route" data-testid="admin-route-save-button"><Save className="h-4 w-4" /></button>
        <button onClick={() => onDelete(r)} className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-red-600" aria-label="Delete route" data-testid="admin-route-delete-button"><Trash2 className="h-4 w-4" /></button>
      </div>
    </td>
  </tr>
);

export const RoutesTab = () => {
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState(EMPTY);

  useEffect(() => {
    api.get("/admin/routes").then((r) => setRows(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const change = (r) => setRows((xs) => xs.map((x) => (x.id === r.id ? r : x)));
  const save = async (r) => {
    try {
      const { data } = await api.put(`/admin/routes/${r.id}`, toPayload(r));
      change(data);
      toast.success("Route saved");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const remove = async (r) => {
    if (!window.confirm(`Delete ${r.origin} → ${r.destination}?`)) return;
    try {
      await api.delete(`/admin/routes/${r.id}`);
      setRows((xs) => xs.filter((x) => x.id !== r.id));
      toast.success("Route deleted");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const add = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/routes", toPayload(draft));
      setRows((xs) => [...xs, data]);
      setDraft(EMPTY);
      toast.success("Route added");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="admin-routes-tab">
      <p className="max-w-2xl text-sm text-slate-600">Prices are per vehicle. Leave the price empty to show "Quote on request". Routes marked <em>Placeholder price</em> were added as examples: edit and save them to confirm.</p>
      <div className="card-soft mt-5 overflow-x-auto border border-slate-200 bg-white">
        <table className="w-full min-w-[820px] text-left text-sm">
          <thead className="border-b border-slate-200 text-[11px] uppercase tracking-[0.16em] text-slate-500">
            <tr>{["From", "To", "Price (€)", "Status", "Visible", ""].map((h) => <th key={h} className="px-3 py-3 font-medium">{h}</th>)}</tr>
          </thead>
          <tbody>{rows.map((r) => <RouteRow key={r.id} r={r} onChange={change} onSave={save} onDelete={remove} />)}</tbody>
        </table>
      </div>
      <form onSubmit={add} className="card-soft mt-6 grid gap-3 border border-dashed border-slate-300 bg-white p-5 md:grid-cols-[1fr_1fr_140px_auto]" data-testid="admin-route-add-form">
        <input className="field-input" placeholder="From" value={draft.origin} onChange={(e) => setDraft({ ...draft, origin: e.target.value })} data-testid="admin-new-route-origin" required />
        <input className="field-input" placeholder="To (e.g. Lindos)" value={draft.destination} onChange={(e) => setDraft({ ...draft, destination: e.target.value })} data-testid="admin-new-route-destination" required />
        <input type="number" min={0} step="0.5" className="field-input" placeholder="Price €" value={draft.price} onChange={(e) => setDraft({ ...draft, price: e.target.value })} data-testid="admin-new-route-price" />
        <button type="submit" className="btn btn-gold" data-testid="admin-add-route-button"><Plus className="h-4 w-4" /> Add route</button>
      </form>
    </div>
  );
};
